import { WorkflowResultContent } from "../workflowData/WorkflowResultContent";
import { ClientBrainProposalCard } from "../clientBrainActions/ClientBrainProposalCard";
import { WorkflowChoiceEditor } from "./WorkflowChoiceEditor";
import type { WorkflowSelectedValues } from "./choiceModel";
import { IbicCodeEditor } from "./IbicCodeEditor";
import { AlertCircle } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Textarea } from "../../components/ui";
import type { DocumentationSignedNoteOption, DocumentationWorkflowNodeResult } from "./models";
import { describeVisibleSourceReferences, signedNoteHref, signedNoteLabel } from "./sourceLabels";
import type { GoalOption, SourceReferenceChip } from "./sourceLabels";
import { getWorkflowNodeContent, groupWorkflowNodeResults, WorkflowNodeEdits } from "./workflowNodeResultUtils";
import { WorkflowEvidence, WorkflowResultBox } from "./WorkflowResultPresentation";
import styles from "./DocumentationWorkspace.module.css";

type Props = {
    nodes: DocumentationWorkflowNodeResult[];
    edits: WorkflowNodeEdits;
    selectedValues?: WorkflowSelectedValues;
    onChoiceEdit?: (nodeId: string, value: string, content: string) => void;
    disabled: boolean;
    readOnly?: boolean;
    allowIbicSelection?: boolean;
    allowChoiceSelection?: boolean;
    confirmations: Set<string>;
    goals?: GoalOption[];
    sourceNotes?: DocumentationSignedNoteOption[];
    onSourceNoteClick?: (noteId: string, projectId?: string) => void;
    onEdit: (nodeId: string, content: string) => void;
    onConfirmation: (nodeId: string, confirmed: boolean) => void;
};

export const WorkflowNodeResults = ({
    nodes,
    edits,
    selectedValues = {},
    onChoiceEdit,
    confirmations,
    disabled,
    readOnly = false,
    allowIbicSelection = false,
    allowChoiceSelection = false,
    goals,
    sourceNotes = [],
    onSourceNoteClick,
    onEdit,
    onConfirmation
}: Props) => {
    const { t, i18n } = useTranslation();

    const chipLabel = (chip: SourceReferenceChip): string => {
        if (chip.kind === "client_record") return t("documentFlow.ref.clientRecord");
        if (chip.kind === "client_brain") return `Client Brain: ${chip.label}`;
        if (chip.kind === "goal") return t("documentFlow.ref.goal", { title: chip.title });
        if (chip.kind === "signed_note") return chip.label || signedNoteLabel(chip.noteId, sourceNotes, i18n.language);
        return chip.label;
    };
    const renderItems = useMemo(() => groupWorkflowNodeResults(nodes), [nodes]);

    if (nodes.length === 0) {
        return (
            <div className={styles.nodeResultsMissing} role="status">
                <AlertCircle size={18} />
                <span>{t("documentFlow.workflowNodeResultsMissing")}</span>
            </div>
        );
    }

    const renderNode = (node: DocumentationWorkflowNodeResult, nested = false) => {
        const sourceLinksOnly = node.display_mode === "source_links";
        const content = getWorkflowNodeContent(node, edits);
        const contentIsEmpty = content.trim().length === 0;
        const hasEvidence = !sourceLinksOnly && node.evidence_spans.length > 0;
        const referenceChips = describeVisibleSourceReferences(node.source_references, sourceLinksOnly, goals);

        const renderReferenceChip = (chip: SourceReferenceChip, index: number) => {
            const key = `${node.node_id}-ref-${index}`;
            if (chip.kind === "client_brain" || chip.kind === "workflow_data") {
                return (
                    <details className={styles.clientBrainReference} key={key}>
                        <summary className={`${styles.refChip} ${styles.refLink}`}>{chipLabel(chip)}</summary>
                        <div className={styles.clientBrainReferenceDetail}>
                            <p>{chip.excerpt}</p>
                            {chip.kind === "client_brain" && chip.version !== undefined ? <small>Client Brain v{chip.version}</small> : null}
                        </div>
                    </details>
                );
            }
            if (chip.kind !== "signed_note" || !onSourceNoteClick) {
                return (
                    <span className={styles.refChip} key={key}>
                        {chipLabel(chip)}
                    </span>
                );
            }

            return (
                <a
                    className={`${styles.refChip} ${styles.refLink}`}
                    href={signedNoteHref(chip.noteId)}
                    key={key}
                    onClick={event => {
                        event.preventDefault();
                        onSourceNoteClick(chip.noteId, chip.projectId);
                    }}
                >
                    {chipLabel(chip)}
                </a>
            );
        };

        return (
            <WorkflowResultBox title={node.title} nested={nested} key={node.node_id}>
                {node.client_brain_proposal && <ClientBrainProposalCard proposal={node.client_brain_proposal} disabled={disabled} notePublication refreshOnMount />}

                {!sourceLinksOnly ? (
                    node.statistics ? <WorkflowResultContent content={content} statistics={node.statistics} /> : node.ibic_editor ? (
                        <IbicCodeEditor config={node.ibic_editor} content={content} disabled={disabled || (readOnly && !allowIbicSelection)}
                            onChange={value => onEdit(node.node_id, value)} />
                    ) : node.output_type === "select" || node.choice_editor ? (
                        <WorkflowChoiceEditor config={node.choice_editor} options={node.choice_editor?.options ?? node.options ?? []}
                            value={Object.prototype.hasOwnProperty.call(selectedValues, node.node_id) ? selectedValues[node.node_id] : node.choice_editor?.selected_value ?? node.selected_value ?? null}
                            title={node.title} content={content} disabled={disabled || !onChoiceEdit || (readOnly && !allowChoiceSelection)}
                            onChange={selection => onChoiceEdit?.(node.node_id, selection.value, selection.content)} />
                    ) : readOnly ? (
                        <WorkflowResultContent content={content || t("documentFlow.emptyNodeContent")} statistics={node.statistics} />
                    ) : (
                        <Textarea
                            className={styles.nodeTextarea}
                            rows={5}
                            value={content}
                            disabled={disabled}
                            aria-invalid={contentIsEmpty}
                            aria-label={t("documentFlow.nodeContentLabel", { title: node.title })}
                            onChange={event => onEdit(node.node_id, event.target.value)}
                        />
                    )
                ) : null}

                {!sourceLinksOnly && !readOnly && contentIsEmpty ? (
                    <p className={styles.nodeContentError}>{t("documentFlow.nodeContentRequired")}</p>
                ) : null}

                {!sourceLinksOnly && node.needs_confirmation && !readOnly ? (
                    <label className={styles.nodeConfirmation}>
                        <input
                            type="checkbox"
                            disabled={disabled}
                            checked={confirmations.has(node.node_id)}
                            onChange={event => onConfirmation(node.node_id, event.target.checked)}
                        />
                        {t("documentFlow.confirmNode")}
                    </label>
                ) : null}

                {hasEvidence || referenceChips.length ? (
                    <div className={styles.nodeProvenance}>
                        {referenceChips.length ? (
                            <div className={`${styles.refChips} ${sourceLinksOnly ? styles.sourceLinksOnly : ""}`}>
                                {!sourceLinksOnly ? <strong>{t("documentFlow.basedOn")}:</strong> : null}
                                {referenceChips.map(renderReferenceChip)}
                            </div>
                        ) : null}
                        {hasEvidence ? <WorkflowEvidence spans={node.evidence_spans} /> : null}
                    </div>
                ) : null}
            </WorkflowResultBox>
        );
    };

    return (
        <div className={styles.workflowNodeResults}>
            {renderItems.map(item => {
                if (item.kind === "node" || item.kind === "orphaned_subflow_step") return renderNode(item.node, item.kind === "orphaned_subflow_step");

                return (
                    <section className={styles.subflowGroup} key={item.node.node_id}>
                        {renderNode(item.node)}
                        {item.steps.length > 0 ? <div className={styles.subflowSteps}>{item.steps.map(step => renderNode(step, true))}</div> : null}
                    </section>
                );
            })}
        </div>
    );
};
